import React, { createContext, Component } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {Alert, Dimensions, Platform} from 'react-native';
import { getAccessToken } from './storage';
import ErrorReportingService from './ErrorReportingService';
import Observables from "@happy-gastro/global-observables";
import { 
    registerBackgroundTasks, 
    triggerSystemDataFetch, 
    triggerBookingsFetch, 
    checkSystemDataUpdated,
    checkBookingsUpdated
} from './BackgroundTasks';

export const SystemContext = createContext();

class SystemProvider extends Component {
    state = {
        foods: [],
        categories: [],
        events: [],
        restaurant: null,
        restaurantPlace: null,
        loaded: false,
        defaultView: 'categories',
        eventColumnCount: 2,
        categoryColumnCount: 2,
        productDisplayMode: 'list', // 'list' or 'grid'
        productColumnCount: 2, // Default number of columns for product grid view
        selectedEvent: null,
        selectedCategory: null,
        token: null,
        vipCards: [],
        bookings: [],
        admins: [],
        printerEvents: [],
        printers: [],
        todoList: []
    };

    // Set default column counts based on screen width
    setDefaultColumnCounts = () => {
        const { width } = Dimensions.get('window');
        let categoryCount, productCount, eventCount;

        if (width < 480) { // Mobile
            categoryCount = 1;
            productCount = 1;
            eventCount = 1;
        } else if (width < 768) { // Intermediate
            categoryCount = 2;
            productCount = 2;
            eventCount = 2;
        } else { // Wide screen
            categoryCount = 3;
            productCount = 3;
            eventCount = 3;
        }

        this.setState({ 
            categoryColumnCount: categoryCount,
            productColumnCount: productCount,
            eventColumnCount: eventCount
        });

        AsyncStorage.setItem('categoryColumnCount', String(categoryCount));
        AsyncStorage.setItem('productColumnCount', String(productCount));
        AsyncStorage.setItem('eventColumnCount', String(eventCount));
    };

    fetchStoredDatas = async () => {
        //const storedFoods = await AsyncStorage.getItem('foods');
        const storedCategories = await AsyncStorage.getItem('categories');
        const storedEvents = await AsyncStorage.getItem('events');
        const storedPrinters = await AsyncStorage.getItem('printers');
        const storedPrinterEvents = await AsyncStorage.getItem('printerEvents');
        const storedTodoList = await AsyncStorage.getItem('todoList');

        if (storedTodoList) {
            this.setState({ todoList: JSON.parse(storedTodoList) });
        } else {
            const defaultTodoList = [
                { description: "Étterem nyitása", time: "10:00", completed: false },
                { description: "Készlet ellenőrzése", time: "10:00", completed: false },
                { description: "Konyha takarítása", time: "10:00", completed: false },
                { description: "Ebédszolgáltatás", time: "12:00", completed: false },
                { description: "Készletek feltöltése", time: "14:00", completed: false },
                { description: "Vacsoraszolgáltatás", time: "18:00", completed: false },
                { description: "Étterem zárása", time: "22:00" , completed: false}
            ];
            this.setState({ todoList: defaultTodoList });
            await AsyncStorage.setItem('todoList', JSON.stringify(defaultTodoList));
        }
        //if (storedFoods) this.setState({ foods: JSON.parse(storedFoods) });
        if (storedCategories) this.setState({ categories: JSON.parse(storedCategories) });
        if (storedEvents) this.setState({ events: JSON.parse(storedEvents) });
        if (storedPrinters) this.setState({ printers: JSON.parse(storedPrinters) });
        if (storedPrinterEvents) this.setState({ printerEvents: JSON.parse(storedPrinterEvents) });
    }

      _fetchBookingsFromApi = async()=> {
        try {
            // First check if we have stored bookings
            const storedBookings = await AsyncStorage.getItem('bookings');
            if (storedBookings) {
                const bookingsData = JSON.parse(storedBookings);
                this.setState({ bookings: bookingsData });
            }

            // Trigger background task to fetch fresh bookings
            await triggerBookingsFetch();

            // Check if bookings have been updated in background
            const isUpdated = await checkBookingsUpdated();

            if (isUpdated) {
                // If bookings were updated, fetch the new data from AsyncStorage
                const bookingsData = await AsyncStorage.getItem('bookings');
                if (bookingsData) {
                    const parsedBookings = JSON.parse(bookingsData);
                    this.setState({ bookings: parsedBookings });
                    return parsedBookings;
                }
            }

            return this.state.bookings;
        } catch (error) {
            ErrorReportingService.error("Failed to fetch bookings", { error }, "SystemContext");
            return this.state.bookings;
        }
    }

    fetchSystemData = async () => {
        const { token } = this.state;
        const restaurantData = await AsyncStorage.getItem('restaurant');
        const restaurantPlaceData = await AsyncStorage.getItem('restaurantPlace');
        const storedDefaultView = await AsyncStorage.getItem('defaultView');
        const storedEventColumnCount = await AsyncStorage.getItem('eventColumnCount');
        const storedCategoryColumnCount = await AsyncStorage.getItem('categoryColumnCount');
        const storedProductDisplayMode = await AsyncStorage.getItem('productDisplayMode');
        const storedProductColumnCount = await AsyncStorage.getItem('productColumnCount');

        if (!token || !restaurantData || !restaurantPlaceData) return;

        // Set display mode settings if they exist
        if (storedDefaultView) this.setState({ defaultView: storedDefaultView });
        if (storedEventColumnCount) this.setState({ eventColumnCount: Number(storedEventColumnCount) });
        if (storedCategoryColumnCount) this.setState({ categoryColumnCount: Number(storedCategoryColumnCount) });
        if (storedProductDisplayMode) this.setState({ productDisplayMode: storedProductDisplayMode });
        if (storedProductColumnCount) this.setState({ productColumnCount: Number(storedProductColumnCount) });

        // First load data from AsyncStorage to show something immediately
        await this.fetchStoredDatas();

        // Set initial state with stored data (but don't set loaded to true yet)
        this.setState({
            restaurant: JSON.parse(restaurantData),
            restaurantPlace: JSON.parse(restaurantPlaceData)
        });

        try {
            // Trigger background task to fetch fresh data
            await triggerSystemDataFetch();

            // Check if data has been updated in background
            const isUpdated = await checkSystemDataUpdated();

            if (isUpdated) {
                // If data was updated, fetch the new data from AsyncStorage
                const foodsData = await AsyncStorage.getItem('foods');
                const categoriesData = await AsyncStorage.getItem('categories');
                const vipCardsData = await AsyncStorage.getItem('vipCards');
                const bookingsData = await AsyncStorage.getItem('bookings');
                const adminsData = await AsyncStorage.getItem('admins');

                // Update state with fresh data and set loaded to true
                this.setState({
                    foods: foodsData ? JSON.parse(foodsData) : [],
                    categories: categoriesData ? JSON.parse(categoriesData) : [],
                    vipCards: vipCardsData ? JSON.parse(vipCardsData) : [],
                    bookings: bookingsData ? JSON.parse(bookingsData) : [],
                    admins: adminsData ? JSON.parse(adminsData) : [],
                    loaded: true
                });
            } else {
                // If no update, still set loaded to true
                this.setState({ loaded: true });
            }
        } catch (error) {
            ErrorReportingService.error("Failed to fetch system data", { error }, "SystemContext");
            // We already loaded data from storage, so the app can still function
            // Set loaded to true even if there was an error
            this.setState({ loaded: true });
        }
    };

    addTodo = async (todo) => {
        const updatedTodoList = [...this.state.todoList, todo];
        this.setState({ todoList: updatedTodoList });
        await AsyncStorage.setItem('todoList', JSON.stringify(updatedTodoList));
    };

    async componentDidMount() {
        const token = await getAccessToken();
        const printersStored = await AsyncStorage.getItem('printers');
        this.setState({ token: token, printers: printersStored ? JSON.parse(printersStored) : [] });

        // Check if column counts are already set in AsyncStorage
        const storedCategoryColumnCount = await AsyncStorage.getItem('categoryColumnCount');
        const storedProductColumnCount = await AsyncStorage.getItem('productColumnCount');
        const storedEventColumnCount = await AsyncStorage.getItem('eventColumnCount');

        // If any column count is not set, set default column counts based on screen size
        if (!storedCategoryColumnCount || !storedProductColumnCount || !storedEventColumnCount) {
            this.setDefaultColumnCounts();
        }

        // Note: Background tasks are now registered in App.jsx to avoid duplicate registration
        // We only need to trigger the fetch here

        Observables.register('fetchSystemData', () => new Promise(() => this.fetchSystemData()));
        this.fetchSystemData();
    }


    componentDidUpdate(prevProps, prevState) {
        if (prevState.token !== this.state.token) {
            this.fetchSystemData();
        }
        if (prevState.printerEvents !== this.state.printerEvents) {
            AsyncStorage.setItem('printerEvents', JSON.stringify(this.state.printerEvents));
        }

        if (prevState.printers !== this.state.printers) {
            AsyncStorage.setItem('printers', JSON.stringify(this.state.printers)).then(() => {
                //alert('Nyomtatók észlelve!');
            });
        }
    }

    render() {
        const { children } = this.props;
        return (
            <SystemContext.Provider value={{
                ...this.state,
                fetchSystemData: this.fetchSystemData,
                fetchBookingsFromApi: this._fetchBookingsFromApi,
                addTodo: this.addTodo,
                setSelectedCategory: category => this.setState({ selectedCategory: category }),
                setSelectedEvent: event => this.setState({ selectedEvent: event }),
                setPrinters: (p) => {
                    this.setState({printers: p})
                },
                setPrinterEvents: (pE) => this.setState({ printerEvents: pE }),
                setProductDisplayMode: (mode) => {
                    this.setState({ productDisplayMode: mode });
                    AsyncStorage.setItem('productDisplayMode', mode);
                },
                setProductColumnCount: (count) => {
                    const numCount = Number(count);
                    this.setState({ productColumnCount: numCount });
                    AsyncStorage.setItem('productColumnCount', String(numCount));
                },
                setCategoryColumnCount: (count) => {
                    const numCount = Number(count);
                    this.setState({ categoryColumnCount: numCount });
                    AsyncStorage.setItem('categoryColumnCount', String(numCount));
                },
                setEventColumnCount: (count) => {
                    const numCount = Number(count);
                    this.setState({ eventColumnCount: numCount });
                    AsyncStorage.setItem('eventColumnCount', String(numCount));
                },
            }}>
                {children}
            </SystemContext.Provider>
        );
    }
}

export default React.memo(SystemProvider);
